import React, { useContext, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  Divider,
  Flex,
  FloatButton,
  Form,
  Input,
  InputNumber,
  Layout as ALayout,
  Modal,
  notification,
  Row,
  Select,
  Switch,
  Tabs,
} from "antd";
import { TYPOGRAPHY_SIZES } from "theme/constants";
import { Text } from "theme/typography";
import {
  defaultProject,
  DraftProjectContext,
  ProjectContext,
  UserContext,
} from "services/contexts";
import { IUser } from "models/auth";
import { topologies } from "services/fixtures/topologies";
import { LuMessageSquareReply } from "react-icons/lu";

import FloatingSideCard from "./components/FloatingSideCard/FloatingSideCard";

import { IFeedback, IProject, ParamField } from "../../models/project";
import {
  useAddFeedbackMutation,
  useAddProjectMutation,
  useGetProjectsQuery,
} from "../../redux/slices/project";
import {
  DOUBLE_CAGE_LOWER_BLOCK_FIRST_KEY,
  DOUBLE_CAGE_UPPER_BLOCK_FIRST_KEY,
  INDUCTION_MACHINE_TOPOLOGY,
  ROTOR_SPECIFICATION_GROUP,
  shouldShowDoubleCageRotorDividers,
} from "services/inductionDoubleCageRotorDividers";

const { Content } = ALayout;

interface ITopology {
  topology: string;
  groups: IGroup[];
  description: string;
}

interface IGroup {
  name: string;
  parameters: IParameter[];
}

type Rule =
  | { field: string; equals: any }
  | { field: string; in: any[] }
  | { all: Rule[] }
  | { any: Rule[] }
  | { not: Rule };

interface IParameter {
  parameter: string;
  key: string;
  defaultValue: string | number | boolean;
  description: string;
  type: string;
  options?: string[];
  visibleWhen?: Rule;
  /** When the rule matches, only these options are allowed (option type). */
  restrictOptionsWhen?: { when: Rule; options: string[] };
  /** When the rule matches, the field is forced to this value (any type). */
  valueWhen?: { when: Rule; value: string | number | boolean };
  /** Min value for number type (default 0 when omitted). */
  min?: number;
  /** Max value for number type (default 1000 when omitted). */
  max?: number;
}

type FeedbackFieldType = {
  author: string;
  comment: string;
};

const isVisible = (rule: Rule | undefined, values: Record<string, any>): boolean => {
  if (!rule) return true;
  if ("field" in rule && "equals" in rule) return values[rule.field] === rule.equals;
  if ("field" in rule && "in" in rule) return rule.in.includes(values[rule.field]);
  if ("all" in rule) return rule.all.every((r) => isVisible(r, values));
  if ("any" in rule) return rule.any.some((r) => isVisible(r, values));
  if ("not" in rule) return !isVisible(rule.not, values);
  return true;
};

/** Effective options for an option param: restricted list when restrictOptionsWhen matches. */
const getEffectiveOptions = (param: IParameter, values: Record<string, any>): string[] | undefined => {
  const opts = param.options;
  if (!opts?.length) return opts;
  const r = param.restrictOptionsWhen;
  if (!r || !isVisible(r.when, values)) return opts;
  return r.options.filter((o) => opts.includes(o));
};

// ✅ normalize option defaults to EXACT option values (fixes blank Select)
const normalizeOption = (dv: any, opts?: string[]) => {
  if (!opts || opts.length === 0) return dv ?? undefined;
  const options = opts.map((o) => String(o).trim());
  if (dv == null || dv === "") return options[0];

  const raw = String(dv).trim();
  if (options.includes(raw)) return raw;

  const low = raw.toLowerCase();
  const found = options.find((o) => o.toLowerCase() === low);
  return found ?? options[0];
};

const buildInitials = (topology?: ITopology): Record<string, any> => {
  if (!topology) return {};

  const initials: Record<string, any> = {};
  topology.groups.forEach((g) => {
    g.parameters.forEach((p) => {
      const t = String(p.type).trim().toLowerCase();
      if (t === "number") {
        const n = typeof p.defaultValue === "number" ? p.defaultValue : Number(p.defaultValue);
        initials[p.key] = Number.isFinite(n) ? n : 0;
      } else if (t === "boolean") {
        initials[p.key] = Boolean(p.defaultValue);
      } else if (t === "option") {
        initials[p.key] = normalizeOption(p.defaultValue, p.options);
      } else {
        initials[p.key] = p.defaultValue ?? "";
      }
    });
  });
  return initials;
};

const Layout = () => {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1365);
  const rawUser = localStorage.getItem("user");
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);

  const [selectedTopology, setSelectedTopology] = useState<ITopology>();

  const [form] = Form.useForm<Partial<ParamField>>();
  const allValues = Form.useWatch([], form) || {};

  const [feedbackForm] = Form.useForm();
  const [addFeedback] = useAddFeedbackMutation();
  const [feedbackComment, setFeedbackComment] = useState<string>("");

  const { data: projects } = useGetProjectsQuery();
  const { setDraftProject } = useContext(DraftProjectContext);
  const { setSelectedProject } = useContext(ProjectContext);

  const [addProject, { isLoading: isCreatingProject }] = useAddProjectMutation();
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    if (rawUser == undefined) {
      navigate("/login");
    } else {
      const cUser = JSON.parse(rawUser) as IUser;
      setUser(cUser);
    }
  }, [rawUser, navigate, setUser]);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 1365);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showModal = () => setOpenModal(true);
  const onClose = () => setOpenModal(false);

  const showFeedbackModal = () => setOpenFeedbackModal(true);
  const onFeedbackClose = () => setOpenFeedbackModal(false);

  const getComponentByField = (
    param: IParameter,
    opts?: { effectiveOptions?: string[]; disabled?: boolean }
  ) => {
    const t = String(param.type).trim().toLowerCase();
    const options = opts?.effectiveOptions ?? param.options;
    const disabled = opts?.disabled ?? false;
    switch (t) {
      case "number":
        return (
          <InputNumber
            min={param.min ?? 0}
            max={param.max ?? 1000}
            style={{ width: "100%" }}
            disabled={disabled}
          />
        );
      case "boolean":
        return <Switch disabled={disabled} />;
      case "option":
        return (
          <Select
            style={{ width: "100%" }}
            options={options?.map((o) => ({ label: o, value: o }))}
            disabled={disabled}
          />
        );
      default:
        return <Input placeholder={param.parameter} disabled={disabled} />;
    }
  };

  const onSelectTopology = (value: string) => {
    const topology = topologies.find((item) => item.topology === value);
    if (topology) setSelectedTopology(topology as ITopology);
  };

  const { TextArea } = Input;

  const handleOk = () => {
    const payload: Partial<IFeedback> = { id: user?.id, comment: feedbackComment };
    addFeedback(payload).unwrap();
    feedbackForm.resetFields();
    onFeedbackClose();
  };

  const handleSubmit = async (values: Partial<ParamField>) => {
    try {
      if (!user?.id) return;

      (values as any)["stator_winding_layer"] = Number((values as any)["stator_winding_layer"]);
      (values as any)["rotor_winding_layer"] = Number((values as any)["rotor_winding_layer"]);
      (values as any)["number_of_rotor_flux_barriers"] = Number((values as any)["number_of_rotor_flux_barriers"]);

      if (String(values["coil_throw"]) === "") {
        (values as any)["coil_throw"] =  Math.round((values as any)["number_of_stator_slots"] / (2 * (values as any)["number_of_pole_pairs"]));
      }
      const payload: Partial<IProject> = {
        topology: selectedTopology?.topology,
        owner: user.id,
        params: values,
      };
      const projectResponse: any = await addProject(payload).unwrap();
      setDraftProject(defaultProject);
      setSelectedProject(projectResponse);
      onClose();
    } catch (error: any) {
      api.error({
        message: <Text weight={600}>Something went wrong while creating your project</Text>,
        description: (
          <Flex vertical>
            <Text>Please check your values:</Text>
            <Text weight={600}>{error?.data?.detail}</Text>
          </Flex>
        ),
        placement: "topRight",
      });
    }
  };

  const handleCreateModalCancel = () => {
    if (!isCreatingProject) onClose();
  };

  // ✅ compute defaults BEFORE rendering the Form
  const initialValues = useMemo(() => buildInitials(selectedTopology), [selectedTopology]);

  // ✅ Sync restrictOptionsWhen: when active, ensure value is in restricted list; sync valueWhen: set field to forced value
  useEffect(() => {
    if (!selectedTopology || !openModal) return;
    const values = allValues as Record<string, any>;
    selectedTopology.groups.forEach((g) => {
      g.parameters.forEach((p) => {
        if (p.restrictOptionsWhen && isVisible(p.restrictOptionsWhen.when, values)) {
          const allowed = p.restrictOptionsWhen.options;
          const current = values[p.key];
          if (allowed.length && current != null && !allowed.includes(String(current).trim())) {
            form.setFieldValue(p.key as keyof ParamField, allowed[0]);
          }
        }
        if (p.valueWhen && isVisible(p.valueWhen.when, values)) {
          form.setFieldValue(p.key as keyof ParamField, p.valueWhen.value);
        }
      });
    });
  }, [selectedTopology, openModal, allValues, form]);

  const tabsItems = useMemo(() => {
    if (!selectedTopology?.groups?.length) return [];
    const topologyName = selectedTopology.topology;
    const useDoubleCageDividers =
      topologyName === INDUCTION_MACHINE_TOPOLOGY && shouldShowDoubleCageRotorDividers(allValues);

    return selectedTopology.groups.map((group) => {
      const upperIdx = group.parameters.findIndex((p) => p.key === DOUBLE_CAGE_UPPER_BLOCK_FIRST_KEY);
      const lowerIdx = group.parameters.findIndex((p) => p.key === DOUBLE_CAGE_LOWER_BLOCK_FIRST_KEY);
      const showDividers = useDoubleCageDividers && group.name === ROTOR_SPECIFICATION_GROUP && upperIdx !== -1 && lowerIdx !== -1;

      let upperDividerPlaced = false;
      let lowerDividerPlaced = false;

      return {
        key: group.name,
        label: group.name,
        forceRender: true,
        children: (
          <Row gutter={18} style={{ width: "100%" }}>
            {group.parameters.flatMap((param, idx) => {
              const visible = isVisible(param.visibleWhen, allValues);
              const effectiveOptions = getEffectiveOptions(param, allValues);
              const valueWhenActive =
                param.valueWhen != null && isVisible(param.valueWhen.when, allValues);

              const out: React.ReactNode[] = [];

              if (showDividers && visible) {
                if (!upperDividerPlaced && idx >= upperIdx && idx < lowerIdx) {
                  upperDividerPlaced = true;
                  out.push(
                    <Col span={24} key={`${group.name}-divider-upper-cage`}>
                      <Divider orientation="left" plain>
                        <Text size={TYPOGRAPHY_SIZES.SMALL} weight={700}>
                          Upper cage
                        </Text>
                      </Divider>
                    </Col>
                  );
                }
                if (!lowerDividerPlaced && idx >= lowerIdx) {
                  lowerDividerPlaced = true;
                  out.push(
                    <Col span={24} key={`${group.name}-divider-lower-cage`}>
                      <Divider orientation="left" plain>
                        <Text size={TYPOGRAPHY_SIZES.SMALL} weight={700}>
                          Lower cage
                        </Text>
                      </Divider>
                    </Col>
                  );
                }
              }

              if (!visible) return out;

              out.push(
                <Col key={param.key} span={8}>
                  <Form.Item<Partial<ParamField>>
                    preserve={false}
                    name={param.key as keyof ParamField}
                    label={
                      <Text size={TYPOGRAPHY_SIZES.SMALL} weight={700}>
                        {param.parameter}
                      </Text>
                    }
                    tooltip={param.description}
                    valuePropName={
                      String(param.type).trim().toLowerCase() === "boolean" ? "checked" : "value"
                    }
                  >
                    {getComponentByField(param, {
                      effectiveOptions,
                      disabled: valueWhenActive,
                    })}
                  </Form.Item>
                </Col>
              );
              return out;
            })}
          </Row>
        ),
      };
    });
  }, [selectedTopology, allValues]);

  return (
    <ALayout style={{ height: "100vh" }}>
      {contextHolder}

      <FloatButton
        shape="circle"
        type="primary"
        icon={<LuMessageSquareReply />}
        onClick={showFeedbackModal}
      />

      <Modal
        onCancel={onFeedbackClose}
        title={"Feedback"}
        open={openFeedbackModal}
        width={{ xs: "90%", sm: "90%", md: "90%", lg: "90%", xl: "50%", xxl: "40%" }}
        onOk={handleOk}
      >
        <Form
          name="add_feedback_modal"
          form={feedbackForm}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item<FeedbackFieldType>
            name="comment"
            label={<strong>Comment</strong>}
            rules={[{ required: true, message: "Please input your comment" }]}
          >
            <TextArea
              placeholder="Your Feedback"
              autoSize={{ minRows: 3, maxRows: 5 }}
              onChange={(e) => setFeedbackComment(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        onCancel={handleCreateModalCancel}
        open={openModal}
        width={{ xs: "90%", sm: "90%", md: "90%", lg: "90%", xl: "50%", xxl: "40%" }}
        footer={null}
        closable={!isCreatingProject}
        maskClosable={!isCreatingProject}
      >
        <Flex vertical gap="large" style={{ paddingTop: 50 }}>
          <Text weight={700} style={{ marginBottom: -15 }}>
            Select a Topology
          </Text>

          <Select
            showSearch
            style={{ width: "100%" }}
            allowClear
            placeholder="Select a Topology"
            onChange={(value) => value && onSelectTopology(value)}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={topologies.map((item) => ({ value: item.topology, label: item.topology }))}
          />

          {selectedTopology ? (
            <Form
              key={selectedTopology.topology}         // ✅ IMPORTANT: remount so defaults apply correctly
              form={form}
              name="create_project_form"
              autoComplete="off"
              layout="vertical"
              requiredMark={false}
              initialValues={initialValues}          // ✅ now Select defaults appear
              onFinish={handleSubmit}
            >
              <Tabs
                key={selectedTopology.topology}
                defaultActiveKey={selectedTopology.groups[0].name}
                destroyOnHidden={false}
                style={{ width: "100%" }}
                items={tabsItems}
              />

              <Flex justify="end">
                <Form.Item style={{ marginTop: 50 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isCreatingProject}
                    disabled={isCreatingProject}
                    style={{ width: "100%" }}
                  >
                    Create Project
                  </Button>
                </Form.Item>
              </Flex>
            </Form>
          ) : null}
        </Flex>
      </Modal>

      <Content style={{ padding: isSmallScreen ? "24px 8px" : "24px" }}>
          <Row gutter={24}>
            <Col span={isSmallScreen ? 5 : 4}>
                <FloatingSideCard showModal={showModal} projects={projects as IProject[]} />
            </Col>
            <Col span={isSmallScreen ? 19 : 20}>
              <Outlet />
            </Col>
          </Row>
      </Content>
    </ALayout>
  );
};

export default Layout;